import React, { useEffect } from 'react';
import { useSmoothScroll } from '../../hooks/useScrollAnimation';

const AnimationEnhancer: React.FC = () => {
  useSmoothScroll();

  useEffect(() => {
    // Add scroll progress indicator
    const progressBar = document.createElement('div');
    progressBar.id = 'scroll-progress';
    progressBar.className = 'fixed top-0 left-0 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 z-50 transition-all duration-300';
    progressBar.style.width = '0%';
    document.body.appendChild(progressBar);

    const updateScrollProgress = () => {
      const scrolled = window.pageYOffset;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrolled / maxScroll) * 100;
      progressBar.style.width = `${Math.min(progress, 100)}%`;
    };

    window.addEventListener('scroll', updateScrollProgress);

    // Add back to top button
    const backToTopButton = document.createElement('button');
    backToTopButton.id = 'back-to-top';
    backToTopButton.innerHTML = `
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
      </svg>
    `;
    backToTopButton.className = 'fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 z-50 opacity-0 pointer-events-none';
    backToTopButton.setAttribute('aria-label', 'Back to top');
    
    backToTopButton.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });

    document.body.appendChild(backToTopButton);

    const toggleBackToTop = () => {
      if (window.pageYOffset > 500) {
        backToTopButton.style.opacity = '1';
        backToTopButton.style.pointerEvents = 'auto';
      } else {
        backToTopButton.style.opacity = '0';
        backToTopButton.style.pointerEvents = 'none';
      }
    };

    window.addEventListener('scroll', toggleBackToTop);

    // Add cursor trail effect (optional - can be disabled)
    let trail: HTMLElement[] = [];
    const maxTrailLength = 20;

    const createTrailDot = (x: number, y: number) => {
      const dot = document.createElement('div');
      dot.className = 'fixed pointer-events-none w-2 h-2 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-full opacity-70 z-10';
      dot.style.left = `${x}px`;
      dot.style.top = `${y}px`;
      dot.style.transform = 'translate(-50%, -50%)';
      document.body.appendChild(dot);

      trail.push(dot);

      if (trail.length > maxTrailLength) {
        const oldDot = trail.shift();
        if (oldDot) {
          oldDot.remove();
        }
      }

      // Animate trail dots
      trail.forEach((trailDot, index) => {
        const opacity = (index + 1) / trail.length * 0.7;
        const scale = (index + 1) / trail.length * 0.8;
        trailDot.style.opacity = opacity.toString();
        trailDot.style.transform = `translate(-50%, -50%) scale(${scale})`;
      });

      // Remove dot after animation
      setTimeout(() => {
        if (dot.parentNode) {
          dot.remove();
        }
      }, 1000);
    };

    let mouseX = 0;
    let mouseY = 0;
    let trailTimer: NodeJS.Timeout;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      clearTimeout(trailTimer);
      trailTimer = setTimeout(() => {
        createTrailDot(mouseX, mouseY);
      }, 50);
    };

    // Only add cursor trail on desktop
    if (window.innerWidth > 1024) {
      document.addEventListener('mousemove', handleMouseMove);
    }

    // Add scroll-triggered animations to existing elements
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fadeInUp');
          entry.target.classList.remove('opacity-0');
        }
      });
    }, observerOptions);

    // Observe all sections and cards
    const elementsToObserve = document.querySelectorAll('section, .card, .card-feature, .card-modern');
    elementsToObserve.forEach((el) => {
      el.classList.add('opacity-0');
      scrollObserver.observe(el);
    });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', updateScrollProgress);
      window.removeEventListener('scroll', toggleBackToTop);
      if (window.innerWidth > 1024) {
        document.removeEventListener('mousemove', handleMouseMove);
      }
      clearTimeout(trailTimer);
      scrollObserver.disconnect();

      // Remove added elements
      const progressBarEl = document.getElementById('scroll-progress');
      const backToTopEl = document.getElementById('back-to-top');
      if (progressBarEl) progressBarEl.remove();
      if (backToTopEl) backToTopEl.remove();

      // Clean up trail dots
      trail.forEach(dot => {
        if (dot.parentNode) {
          dot.remove();
        }
      });
    };
  }, []);

  return null;
};

export default AnimationEnhancer;